import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import '@/App.css';
import { trackPageView, trackNavigation, logActivity } from './utils/activityTracker';
import Home from './pages/Home';
import EmailPreferencesPage from './pages/EmailPreferencesPage';
import ContactUs from './pages/ContactUs';
import MyRoster from './pages/MyRoster';
import Scorecard from './pages/Scorecard';
import Leaderboard from './pages/Leaderboard';
import EventLog from './pages/EventLog';
import LoginPage from './pages/LoginPage';
import CreateLeaguePage from './pages/CreateLeaguePage';
import AcceptInvite from './pages/AcceptInvite';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminPlayers from './pages/admin/AdminPlayers';
import InvitePlayers from './pages/admin/InvitePlayers';
import PlayerDetail from './pages/admin/PlayerDetail';
import AuditLog from './pages/admin/AuditLog';
import AdminContestants from './pages/admin/AdminContestants';
import AdminScoringRules from './pages/admin/AdminScoringRules';
import AdminEventLog from './pages/admin/AdminEventLog';
import AdminEpisodeCalendar from './pages/admin/AdminEpisodeCalendar';
import AdminNotes from './pages/admin/AdminNotes';
import AdminBonusQuestions from './pages/admin/AdminBonusQuestions';
import AdminLeagueOptions from './pages/admin/AdminLeagueOptions';
import AdminImport from './pages/admin/AdminImport';
import AdminSeasonDraft from './pages/admin/AdminSeasonDraft';
import PullToRefreshTest from './pages/PullToRefreshTest';
import PublicLandingPage from './pages/PublicLandingPage';
import HowItWorksPage from './pages/HowItWorksPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PlayerDraftRankings from './pages/PlayerDraftRankings';
import DraftReplay from './pages/DraftReplay';
// New public pages for AdSense
import RulesPage from './pages/RulesPage';
import FAQPage from './pages/FAQPage';
import AboutPage from './pages/AboutPage';
import BlogIndexPage from './pages/BlogIndexPage';
import BlogHowToWin from './pages/blog/BlogHowToWin';
import BlogDraftStrategy from './pages/blog/BlogDraftStrategy';
import BlogAnatomyOfSeason from './pages/blog/BlogAnatomyOfSeason';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
// Super Admin pages
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import SuperAdminTenants from './pages/super-admin/SuperAdminTenants';
import SuperAdminTenantDetail from './pages/super-admin/SuperAdminTenantDetail';
import SuperAdminUsers from './pages/super-admin/SuperAdminUsers';
import SuperAdminUserDetail from './pages/super-admin/SuperAdminUserDetail';
import SuperAdminUserLogs from './pages/super-admin/SuperAdminUserLogs';
import SuperAdminIssuesLog from './pages/super-admin/SuperAdminIssuesLog';
import SuperAdminSettings from './pages/super-admin/SuperAdminSettings';
import SuperAdminIntegrations from './pages/super-admin/SuperAdminIntegrations';
import SuperAdminNotifications from './pages/super-admin/SuperAdminNotifications';
import SuperAdminBetaFeatures from './pages/super-admin/SuperAdminBetaFeatures';
import SuperAdminFeatureFlags from './pages/super-admin/SuperAdminFeatureFlags';
import SuperAdminProfile from './pages/super-admin/SuperAdminProfile';
import SuperAdminSMS from './pages/super-admin/SuperAdminSMS';
import SuperAdminEmail from './pages/super-admin/SuperAdminEmail';
import SuperAdminEmailTriggers from './pages/super-admin/SuperAdminEmailTriggers';
import SuperAdminDripCampaigns from './pages/super-admin/SuperAdminDripCampaigns';
import SuperAdminDefaultContestants from './pages/super-admin/SuperAdminDefaultContestants';
import SuperAdminAnnouncements from './pages/super-admin/SuperAdminAnnouncements';
// New consolidated Communications pages
import CommsOverview from './pages/super-admin/CommsOverview';
import CommsNotifications from './pages/super-admin/CommsNotifications';
import CommsChannels from './pages/super-admin/CommsChannels';
import CommsTemplates from './pages/super-admin/CommsTemplates';
import CommsLogs from './pages/super-admin/CommsLogs';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import OnboardingModal from './components/OnboardingModal';
import HelpButton from './components/HelpButton';
import { resolveSeason, rememberSeason } from './utils/seasonDefault';
import SuperAdminScoringDiagnostics from './pages/super-admin/SuperAdminScoringDiagnostics';
import NotFoundPage from './pages/NotFoundPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get stored session token
const getStoredToken = () => localStorage.getItem('session_token');
const setStoredToken = (token) => {
  if (token) {
    localStorage.setItem('session_token', token);
  } else {
    localStorage.removeItem('session_token');
  }
};

// Create axios instance with interceptor for auth
const createAuthenticatedRequest = () => {
  const instance = axios.create();
  
  // Add auth header to all requests
  instance.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    config.withCredentials = true;
    return config;
  });
  
  return instance;
};

const authAxios = createAuthenticatedRequest();

export const AppContext = React.createContext();

function AuthWrapper() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seasons, setSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [tenantContext, setTenantContext] = useState(null);
  const sessionProcessedRef = React.useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const loadTenantContext = async () => {
    try {
      const response = await authAxios.get(`${API}/me/tenant`);
      setTenantContext(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load tenant context:', error);
      return null;
    }
  };

  const processSessionId = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, {}, {
        headers: { 
          'X-Session-ID': sessionId,
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      const userData = response.data.user || response.data;
      const sessionToken = response.data.session_token;
      
      if (!userData || !userData.id) {
        throw new Error('Invalid user data received');
      }
      
      // Store session token for mobile compatibility - do this FIRST
      if (sessionToken) {
        setStoredToken(sessionToken);
      }
      
      // Clean URL - remove the hash
      window.history.replaceState({}, document.title, window.location.pathname);
      sessionStorage.removeItem('pending_session_id');
      
      // Load tenant context with the new session token
      let tenantData = null;
      try {
        const tenantResponse = await axios.get(`${API}/me/tenant`, {
          headers: { 'Authorization': `Bearer ${sessionToken}` },
          withCredentials: true
        });
        tenantData = tenantResponse.data;
        setTenantContext(tenantData);
      } catch (tenantError) {
        console.error('Failed to load tenant context:', tenantError);
      }
      
      // Set user state AFTER token is stored
      setUser(userData);
      setLoading(false);
      
      // Track login activity (skip for Super Admins)
      if (!userData.is_super_admin) {
        logActivity('login', 'oauth_login', { provider: 'google' });
      }
      
      if (!userData.onboarding_completed) {
        setShowOnboarding(true);
      } else {
        toast.success(`Welcome back, ${userData.name}!`);
      }
      
      // Route based on user type
      if (userData.is_super_admin) {
        navigate('/super-admin');
      } else if (tenantData?.needs_league) {
        navigate('/create-league');
      } else if (tenantData?.is_league_admin && !tenantData?.has_seasons) {
        // League Admin with no seasons - take them to admin to create a season
        navigate('/admin');
      } else {
        // Always navigate to /home after OAuth, even if already on /home path
        // This ensures the app state is properly initialized
        navigate('/home');
      }
    } catch (error) {
      console.error('Session creation failed:', error);
      toast.error('Authentication failed. Please try logging in again.');
      setLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
      sessionStorage.removeItem('pending_session_id');
      navigate('/login');
    }
  };

  const checkExistingSession = async () => {
    const token = getStoredToken();
    console.log('Checking existing session, token exists:', !!token);
    
    if (!token) {
      console.log('No token found, setting loading to false');
      setLoading(false);
      return;
    }
    
    try {
      const response = await authAxios.get(`${API}/auth/me`);
      console.log('Session valid, user:', response.data?.email);
      setUser(response.data);
      // Check if returning user hasn't completed onboarding
      if (!response.data.onboarding_completed) {
        setShowOnboarding(true);
      }
      // Load tenant context
      await loadTenantContext();
      setLoading(false);
    } catch (error) {
      console.log('Session invalid or error:', error.message);
      // Clear stored token if session is invalid
      setStoredToken(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prevent double processing
    if (sessionProcessedRef.current) return;
    
    const hash = window.location.hash;
    const storedSessionId = sessionStorage.getItem('pending_session_id');
    const freshLogin = sessionStorage.getItem('fresh_login');
    
    // Clear fresh login flag immediately
    if (freshLogin) {
      sessionStorage.removeItem('fresh_login');
    }
    
    if (hash && hash.includes('session_id=')) {
      sessionProcessedRef.current = true;
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      sessionStorage.setItem('pending_session_id', sessionId);
      processSessionId(sessionId);
    } else if (storedSessionId) {
      sessionProcessedRef.current = true;
      sessionStorage.removeItem('pending_session_id');
      processSessionId(storedSessionId);
    } else {
      // Check existing session (includes fresh login from invite)
      checkExistingSession();
    }
  }, []);

  const handleOnboardingComplete = (updatedUser) => {
    setUser(updatedUser);
    setShowOnboarding(false);
  };

  const logout = async () => {
    try {
      // Track logout before clearing session (skip for Super Admins)
      if (user && !user.is_super_admin) {
        await logActivity('logout', 'user_logout');
      }
      
      await authAxios.post(`${API}/auth/logout`, {});
      setStoredToken(null);
      setUser(null);
      setTenantContext(null);
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still clear local state even if server logout fails
      setStoredToken(null);
      setUser(null);
      setTenantContext(null);
      navigate('/');
    }
  };

  const loadSeasons = async () => {
    try {
      const response = await authAxios.get(`${API}/seasons`);
      setSeasons(response.data);
      if (response.data.length > 0 && !currentSeason) {
        setCurrentSeason(resolveSeason(response.data, user?.id));
      }
    } catch (error) {
      console.error('Failed to load seasons:', error);
    }
  };

  useEffect(() => {
    if (user && !user.is_super_admin) {
      loadSeasons();
    }
  }, [user]); // Load seasons when user changes

  // Track page views when location changes
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    // Skip activity tracking for Super Admins
    if (user && !user.is_super_admin) {
      const currentPath = location.pathname;
      const prevPath = prevPathRef.current;
      
      // Track page view
      trackPageView(currentPath);
      
      // Track navigation if path changed
      if (prevPath !== currentPath) {
        trackNavigation(prevPath, currentPath);
        prevPathRef.current = currentPath;
      }
    }
  }, [location.pathname, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Public routes that don't require authentication
  const publicPaths = [
    '/', '/login', '/forgot-password', '/reset-password', '/how-it-works',
    '/rules', '/faq', '/about', '/blog', '/privacy', '/terms'
  ];
  const isPublicBlogPost = location.pathname.startsWith('/blog/');
  const hasSessionId = window.location.hash.includes('session_id=');
  const isInvitePath = location.pathname.startsWith('/invite/');
  
  if (!user && !publicPaths.includes(location.pathname) && !isPublicBlogPost && !hasSessionId && !isInvitePath) {
    return <Navigate to="/" />;
  }

  const contextValue = {
    user,
    logout,
    seasons,
    currentSeason,
    setCurrentSeason: (season) => {
      rememberSeason(user?.id, season?.id);
      setCurrentSeason(season);
    },
    loadSeasons,
    API,
    BACKEND_URL,
    authAxios, // Authenticated axios instance for mobile compatibility
    isDemo: user?.is_demo_user || false,
    tenantContext,
    loadTenantContext,
    isSuperAdmin: user?.is_super_admin || false,
    userRole: tenantContext?.membership?.role || user?.role || 'player'
  };

  const handleLoginSuccess = async (userData) => {
    setUser(userData);
    
    // Load tenant context
    try {
      const tenantRes = await authAxios.get(`${API}/me/tenant`);
      setTenantContext(tenantRes.data);
      
      // Route based on user type
      if (userData.is_super_admin) {
        navigate('/super-admin');
      } else if (tenantRes.data.needs_league) {
        navigate('/create-league');
      } else if (tenantRes.data.is_league_admin && !tenantRes.data.has_seasons) {
        // League Admin with no seasons - take them to admin to create a season
        loadSeasons();
        navigate('/admin');
      } else {
        loadSeasons();
        navigate('/home');
      }
    } catch (error) {
      loadSeasons();
    }
  };

  // Determine where to redirect logged in users
  const getDefaultRoute = () => {
    if (!user) return '/';
    if (user.is_super_admin) return '/super-admin';
    if (tenantContext?.needs_league) return '/create-league';
    if (tenantContext?.is_league_admin && !tenantContext?.has_seasons) return '/admin';
    return '/home';
  };

  return (
    <AppContext.Provider value={contextValue}>
      <div className="App">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={user ? <Navigate to={getDefaultRoute()} /> : <PublicLandingPage API={API} />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogIndexPage />} />
          <Route path="/blog/how-to-win-survivor-fantasy-league" element={<BlogHowToWin />} />
          <Route path="/blog/draft-day-strategy" element={<BlogDraftStrategy />} />
          <Route path="/blog/anatomy-of-a-survivor-fantasy-season" element={<BlogAnatomyOfSeason />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} /> : <LoginPageWrapper onLoginSuccess={handleLoginSuccess} />} />
          <Route path="/forgot-password" element={user ? <Navigate to={getDefaultRoute()} /> : <ForgotPasswordPage API={API} />} />
          <Route path="/reset-password" element={user ? <Navigate to={getDefaultRoute()} /> : <ResetPasswordPage API={API} />} />
          <Route path="/invite/:token" element={<AcceptInvite />} />
          
          {/* League creation for new users */}
          <Route path="/create-league" element={
            user && tenantContext?.needs_league ? <CreateLeaguePage /> : <Navigate to={getDefaultRoute()} />
          } />
          
          {/* Super Admin routes */}
          <Route path="/super-admin" element={
            user?.is_super_admin ? <SuperAdminDashboard /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/tenants" element={
            user?.is_super_admin ? <SuperAdminTenants /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/tenants/:tenantId" element={
            user?.is_super_admin ? <SuperAdminTenantDetail /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/users" element={
            user?.is_super_admin ? <SuperAdminUsers /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/users/:userId" element={
            user?.is_super_admin ? <SuperAdminUserDetail /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/user-logs" element={
            user?.is_super_admin ? <SuperAdminUserLogs /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/issues-log" element={
            user?.is_super_admin ? <SuperAdminIssuesLog /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/scoring-diagnostics" element={
            user?.is_super_admin ? <SuperAdminScoringDiagnostics /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/settings" element={
            user?.is_super_admin ? <SuperAdminSettings /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/announcements" element={
            user?.is_super_admin ? <SuperAdminAnnouncements /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/integrations" element={
            user?.is_super_admin ? <SuperAdminIntegrations /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/notifications" element={
            user?.is_super_admin ? <SuperAdminNotifications /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/beta-features" element={
            user?.is_super_admin ? <SuperAdminBetaFeatures /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/sms" element={
            user?.is_super_admin ? <SuperAdminSMS /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/email" element={
            user?.is_super_admin ? <SuperAdminEmail /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/email/triggers" element={
            user?.is_super_admin ? <SuperAdminEmailTriggers /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/email/drip-campaigns" element={
            user?.is_super_admin ? <SuperAdminDripCampaigns /> : <Navigate to="/home" />
          } />
          
          {/* New Consolidated Communications Routes */}
          <Route path="/super-admin/comms" element={
            user?.is_super_admin ? <CommsOverview /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/comms/notifications" element={
            user?.is_super_admin ? <CommsNotifications /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/comms/channels" element={
            user?.is_super_admin ? <CommsChannels /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/comms/templates" element={
            user?.is_super_admin ? <CommsTemplates /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/comms/logs" element={
            user?.is_super_admin ? <CommsLogs /> : <Navigate to="/home" />
          } />
          
          <Route path="/super-admin/settings/feature-flags" element={
            user?.is_super_admin ? <SuperAdminFeatureFlags /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/default-contestants" element={
            user?.is_super_admin ? <SuperAdminDefaultContestants /> : <Navigate to="/home" />
          } />
          <Route path="/super-admin/profile" element={
            user?.is_super_admin ? <SuperAdminProfile /> : <Navigate to="/home" />
          } />
          
          {/* Regular user routes */}
          <Route path="/home" element={<Home />} />
          <Route path="/my-roster" element={<MyRoster />} />
          <Route path="/scorecard" element={<Scorecard />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/event-log" element={<EventLog />} />
          <Route path="/email-preferences" element={<EmailPreferencesPage />} />
          <Route path="/email-preferences/:userId" element={<EmailPreferencesPage />} />
          <Route path="/contact" element={<ContactUs />} />
          
          {/* Admin routes */}
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/players" element={<AdminPlayers />} />
          <Route path="/admin/players/invite" element={<InvitePlayers />} />
          <Route path="/admin/players/audit" element={<AuditLog />} />
          <Route path="/admin/players/:userId" element={<PlayerDetail />} />
          <Route path="/admin/contestants" element={<AdminContestants />} />
          <Route path="/admin/scoring-rules" element={<AdminScoringRules />} />
          <Route path="/admin/event-log" element={<AdminEventLog />} />
          <Route path="/admin/episode-calendar" element={<AdminEpisodeCalendar />} />
          <Route path="/admin/notes" element={<AdminNotes />} />
          <Route path="/admin/bonus-questions" element={<AdminBonusQuestions />} />
          <Route path="/admin/league-options" element={<AdminLeagueOptions />} />
          <Route path="/admin/draft" element={<AdminSeasonDraft />} />
          <Route path="/admin/import" element={<AdminImport />} />
          {/* SMS Notifications moved to Super Admin - redirect old route */}
          <Route path="/admin/notifications" element={
            user?.is_super_admin ? <Navigate to="/super-admin/sms" /> : <Navigate to="/admin" />
          } />
          
          {/* Auto-Draft Routes */}
          <Route path="/draft/rankings" element={<PlayerDraftRankings />} />
          <Route path="/draft/:seasonId/replay" element={<DraftReplay />} />
          
          {/* Test routes */}
          <Route path="/pull-test" element={<PullToRefreshTest />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster position="top-right" richColors />
        
        {/* Floating Help Button - only for signed-in users */}
        {user && <HelpButton user={user} />}
        
        {/* First-time user onboarding */}
        <OnboardingModal
          open={showOnboarding}
          user={user}
          API={API}
          onComplete={handleOnboardingComplete}
        />
      </div>
    </AppContext.Provider>
  );
}

function LoginPageWrapper({ onLoginSuccess }) {
  const currentOrigin = window.location.origin;
  const redirectUrl = encodeURIComponent(`${currentOrigin}/home`);
  const authUrl = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;

  return (
    <LoginPage API={API} authUrl={authUrl} onLoginSuccess={onLoginSuccess} />
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthWrapper />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
